/**
 * Form field set (docs/03 §5, docs/04 §3): labels above fields, required
 * marked, inline errors field-anchored, and the money field that renders from
 * cents and edits in dollars — the UI half of the integer-cents invariant
 * (docs/02, ADR-13).
 */

import { MoneyError, centsToInputvalue, parseDollarsToCents, type Cents } from '@propos/shared';
import { clsx } from 'clsx';
import {
  useId,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';

export interface FieldShellProps {
  readonly label: string;
  readonly required?: boolean | undefined;
  readonly error?: string | undefined;
  readonly hint?: string | undefined;
  /**
   * Hide the visible label while keeping it for assistive tech. For a field in
   * a GRID whose column header already says what it is — repeating "Monday
   * opens" above an input that sits under an "Opens" heading is noise on
   * screen, but a screen reader has no column header to lean on, so the label
   * must still exist.
   *
   * 'sm-up' hides it only where the grid's header row is actually rendered;
   * below that breakpoint the header is gone and the label is all there is.
   */
  readonly labelHidden?: 'always' | 'sm-up' | undefined;
}

function FieldShell({
  label,
  required = false,
  error,
  hint,
  labelHidden,
  inputId,
  children,
}: FieldShellProps & { readonly inputId: string; readonly children: ReactNode }): ReactNode {
  return (
    <div className="flex flex-col gap-1">
      <label
        htmlFor={inputId}
        className={clsx(
          'text-meta font-medium text-ink2',
          labelHidden === 'always' && 'sr-only',
          labelHidden === 'sm-up' && 'sm:sr-only',
        )}
      >
        {label}
        {required ? (
          <span aria-hidden className="text-red-fg">
            {' '}
            *
          </span>
        ) : null}
      </label>
      {children}
      {hint !== undefined && error === undefined ? (
        <span className="text-chip text-ink2">{hint}</span>
      ) : null}
      {error !== undefined ? (
        <span id={`${inputId}-error`} role="alert" className="text-chip font-medium text-red-fg">
          {error}
        </span>
      ) : null}
    </div>
  );
}

/**
 * `keeper-ignore` is not decoration (ADR-40).
 *
 * Matt: "keeper keeps trying to auto fill text boxes in all the forms". The
 * Keeper browser extension injects its own icon and dropdown into anything that
 * looks like a credential field, which here means Entity id, TKC number,
 * lockbox LOCATION — form fields that have nothing to do with passwords. The
 * extension reads this class and skips the element.
 *
 * Safe to apply blanket-wide in THIS app specifically, and worth saying why:
 * PropOS has no password field to autofill. Authentication is Entra SSO and the
 * app never stores a password (docs/01 §1). There is therefore no case where a
 * password manager helping is the behaviour we want, and one field it should
 * never touch — the security-codes reveal, which must not end up in anybody's
 * vault as a side effect.
 */
/**
 * The focus indicator every text control in the app shares.
 *
 * Exported because a control that genuinely needs its own layout — the global
 * search box, with an icon well on each side — still must not invent its own
 * focus treatment, or lose it. Four screens had hand-copied `INPUT_CLASSES`
 * minus this line, so nine filter and search controls fell back to whatever the
 * browser happened to draw (docs/03 §8, WCAG 2.4.7). Losing the ring is the
 * easiest thing to lose in a copy, because nothing looks wrong until somebody
 * navigates by keyboard.
 */
export const FOCUS_RING =
  'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-700';

const INPUT_CLASSES =
  'keeper-ignore min-h-11 w-full rounded-ctl border bg-card px-3 py-2 text-body text-ink ' +
  `placeholder:text-ink2 ${FOCUS_RING}`;

export interface TextFieldProps
  extends FieldShellProps, Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'required'> {}

export function TextField({
  label,
  required,
  error,
  hint,
  labelHidden,
  className,
  ...rest
}: TextFieldProps): ReactNode {
  const id = useId();
  return (
    <FieldShell
      label={label}
      required={required}
      error={error}
      hint={hint}
      labelHidden={labelHidden}
      inputId={id}
    >
      <input
        id={id}
        required={required}
        aria-invalid={error !== undefined || undefined}
        aria-describedby={error !== undefined ? `${id}-error` : undefined}
        className={clsx(
          INPUT_CLASSES,
          error !== undefined ? 'border-red-fg' : 'border-control-line',
          className,
        )}
        {...rest}
      />
    </FieldShell>
  );
}

/**
 * A web address, which a person types the way they say it.
 *
 * Matt, on the QA sheet: *"Creating a new vendor and clicking 'create vendor' I
 * get invalid payload error"*. Reproduced — `website: 'carolinawaste.com'` fails
 * `z.url()`, because a URL without a scheme is not a URL. Nobody says "h t t p s
 * colon slash slash carolina waste dot com", so the form has to meet them:
 *
 *  - **On blur, a scheme-less host gains `https://`.** Not on every keystroke,
 *    which would fight the cursor while somebody is still typing.
 *  - Only when it plausibly IS a host — a dot, no spaces, no scheme already. A
 *    value we cannot repair is left exactly as typed and the server's message
 *    explains it, because silently rewriting somebody's input into something
 *    else is worse than refusing it.
 *  - `mailto:` and other schemes are left alone.
 *
 * Every URL input in the app uses this, so a domain typed into one form cannot
 * be accepted while the same domain in another form is rejected.
 */
export function normalizeWebAddress(value: string): string {
  const trimmed = value.trim();
  if (trimmed === '') return '';
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return trimmed;
  if (/\s/.test(trimmed)) return trimmed;
  if (!/^[^.]+\.[^.]+/.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

export function UrlField({
  value,
  onChange,
  maxLength,
  ...shell
}: FieldShellProps & {
  readonly value: string;
  /** The value, not the event — the same shape `DateField` and `SelectField` use. */
  readonly onChange: (value: string) => void;
  readonly maxLength?: number | undefined;
}): ReactNode {
  const id = useId();
  return (
    <FieldShell {...shell} inputId={id}>
      <input
        id={id}
        type="url"
        inputMode="url"
        value={value}
        required={shell.required}
        maxLength={maxLength}
        aria-invalid={shell.error !== undefined || undefined}
        aria-describedby={shell.error !== undefined ? `${id}-error` : undefined}
        className={clsx(
          INPUT_CLASSES,
          shell.error !== undefined ? 'border-red-fg' : 'border-control-line',
        )}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        onBlur={(event) => {
          // On blur, not per keystroke: adding a scheme mid-word would fight the
          // cursor. The owner is notified like any other edit, so the form's
          // dirty tracking sees it.
          const fixed = normalizeWebAddress(event.target.value);
          if (fixed !== event.target.value) onChange(fixed);
        }}
      />
    </FieldShell>
  );
}

/**
 * Multi-line text. Same shell, same focus ring, same error anchoring as
 * TextField — the only difference is that it grows.
 *
 * `rows` defaults to 3 and the field is vertically resizable but not
 * horizontally: a user dragging it wider would break the form's grid, and the
 * fields this is used for (access notes, critical information) get long
 * downward, not sideways. A character counter appears once `maxLength` is set
 * and the value passes 80% of it — silently truncating what someone typed at a
 * property at 11pm is not acceptable, and a limit you only discover by hitting
 * it is barely better.
 */
export interface TextAreaFieldProps
  extends FieldShellProps, Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id' | 'required'> {}

export function TextAreaField({
  label,
  required,
  error,
  hint,
  className,
  rows = 3,
  maxLength,
  value,
  ...rest
}: TextAreaFieldProps): ReactNode {
  const id = useId();
  const used = typeof value === 'string' ? value.length : 0;
  const nearLimit = maxLength !== undefined && used > maxLength * 0.8;
  return (
    <FieldShell
      label={label}
      required={required}
      error={error}
      hint={nearLimit ? `${String(used)} / ${String(maxLength)} characters` : hint}
      inputId={id}
    >
      <textarea
        id={id}
        rows={rows}
        required={required}
        maxLength={maxLength}
        value={value}
        aria-invalid={error !== undefined || undefined}
        aria-describedby={error !== undefined ? `${id}-error` : undefined}
        className={clsx(
          INPUT_CLASSES,
          'resize-y leading-relaxed',
          error !== undefined ? 'border-red-fg' : 'border-control-line',
          className,
        )}
        {...rest}
      />
    </FieldShell>
  );
}

/**
 * Money: state is integer CENTS or null, never a float. The input shows
 * dollars; parsing rejects a third decimal place rather than rounding it
 * (a typo in a rent figure should stop the save, not change the number).
 */
export function MoneyField({
  label,
  required,
  error,
  hint,
  cents,
  onCentsChange,
}: FieldShellProps & {
  readonly cents: Cents | null;
  readonly onCentsChange: (cents: Cents | null) => void;
}): ReactNode {
  const id = useId();
  const [text, setText] = useState(cents === null ? '' : centsToInputvalue(cents));
  const [parseError, setParseError] = useState<string | undefined>(undefined);
  const shownError = error ?? parseError;

  return (
    <FieldShell label={label} required={required} error={shownError} hint={hint} inputId={id}>
      <div className="relative">
        <span aria-hidden className="absolute left-3 top-1/2 -translate-y-1/2 text-ink3">
          $
        </span>
        <input
          id={id}
          inputMode="decimal"
          value={text}
          required={required}
          aria-invalid={shownError !== undefined || undefined}
          aria-describedby={shownError !== undefined ? `${id}-error` : undefined}
          className={clsx(
            INPUT_CLASSES,
            'pl-7',
            shownError !== undefined ? 'border-red-fg' : 'border-control-line',
          )}
          /**
           * Commit on CHANGE as well as on blur (2026-08-28).
           *
           * It used to commit only on blur, which is invisible until a form
           * gates its Save button on "has anything changed" — and then the only
           * way to change a rent is to type it and click somewhere else first,
           * because clicking a DISABLED button does not blur the input. The
           * lease form (docs/17 3B.4) is the first such form and found it: the
           * button read "Nothing has changed yet" over a freshly typed figure.
           *
           * The text is deliberately NOT reformatted here — that is still the
           * blur's job, because rewriting the box mid-keystroke fights the
           * person typing. A partial value ("12.", "12875.5") simply does not
           * commit yet; blur will parse it or say why.
           */
          onChange={(event) => {
            const next = event.target.value;
            setText(next);
            if (next.trim() === '') {
              setParseError(undefined);
              onCentsChange(null);
              return;
            }
            try {
              onCentsChange(parseDollarsToCents(next));
              setParseError(undefined);
            } catch {
              // Still being typed. Blur is where an unparseable value is named.
            }
          }}
          onBlur={() => {
            if (text.trim() === '') {
              setParseError(undefined);
              onCentsChange(null);
              return;
            }
            try {
              const parsed = parseDollarsToCents(text);
              setParseError(undefined);
              setText(centsToInputvalue(parsed));
              onCentsChange(parsed);
            } catch (parseFailure) {
              setParseError(
                parseFailure instanceof MoneyError
                  ? 'Enter a dollar amount with at most two decimal places'
                  : 'Enter a valid amount',
              );
            }
          }}
        />
      </div>
    </FieldShell>
  );
}

/**
 * Phone input, formatting to the phone standard as you type (docs/02 "The phone
 * standard"): ###-###-####.
 *
 * It does NOT cap at ten digits. The mask used to, which silently refused a
 * four-digit internal line and every international number — and a field that
 * refuses a real number is how the number ends up in a notes field where
 * nothing can search it. Past ten digits the digits stand as dialled, and the
 * server normalizes on save (`normalizePhone`) so the stored form is the same
 * whatever anyone typed.
 */
export function PhoneField({
  label,
  required,
  error,
  hint,
  labelHidden,
  value,
  onChange,
}: FieldShellProps & {
  readonly value: string;
  readonly onChange: (value: string) => void;
}): ReactNode {
  const id = useId();
  const format = (raw: string): string => {
    const digits = raw.replace(/\D/g, '').slice(0, 15);
    if (digits.length > 10) return digits;
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  };
  return (
    <FieldShell
      label={label}
      required={required}
      error={error}
      hint={hint}
      labelHidden={labelHidden}
      inputId={id}
    >
      <input
        id={id}
        type="tel"
        autoComplete="tel"
        value={value}
        required={required}
        aria-invalid={error !== undefined || undefined}
        aria-describedby={error !== undefined ? `${id}-error` : undefined}
        className={clsx(
          INPUT_CLASSES,
          error !== undefined ? 'border-red-fg' : 'border-control-line',
        )}
        onChange={(event) => {
          onChange(format(event.target.value));
        }}
        placeholder="704-555-0100"
      />
    </FieldShell>
  );
}

/** Searchable select for ≥8 options (docs/03 §5); plain listbox below that. */
export function SelectField({
  label,
  required,
  error,
  hint,
  labelHidden,
  value,
  onChange,
  options,
  placeholder = 'Select…',
  disabledReason,
}: FieldShellProps & {
  readonly value: string | null;
  readonly onChange: (value: string) => void;
  readonly options: readonly { value: string; label: string }[];
  readonly placeholder?: string | undefined;
  /**
   * Present ⇒ disabled, and the reason is shown in the hint's place rather than
   * left to guesswork — the same contract Button and CheckboxField already
   * carry (docs/04 §7 lists an unexplained disabled control as an
   * anti-pattern). Added for the agreement form's coverage type, which is
   * genuinely immutable (ADR-04) and needs to SAY so where the control is,
   * rather than disappear and leave "why can't I change this" unanswered.
   */
  readonly disabledReason?: string | undefined;
}): ReactNode {
  const id = useId();
  const disabled = disabledReason !== undefined;
  const searchable = options.length >= 8;
  const listId = `${id}-list`;
  /**
   * Some callers pass their own empty-valued option because it is a real
   * choice — "All", "Not set", "None (top level)", "Any property". Rendering
   * OUR placeholder as well produced two rows that both mean nothing, and
   * because both carry value="" the browser selected the first — so a filter
   * showing every row displayed "Select…" instead of "All". When the caller
   * supplies that option, it IS the placeholder.
   */
  const hasEmptyOption = options.some((option) => option.value === '');

  if (!searchable) {
    return (
      <FieldShell
        label={label}
        required={required}
        error={error}
        hint={disabledReason ?? hint}
        labelHidden={labelHidden}
        inputId={id}
      >
        <select
          id={id}
          value={value ?? ''}
          required={required}
          disabled={disabled}
          aria-invalid={error !== undefined || undefined}
          className={clsx(
            INPUT_CLASSES,
            error !== undefined ? 'border-red-fg' : 'border-control-line',
            disabled ? 'cursor-not-allowed opacity-60' : '',
          )}
          onChange={(event) => {
            onChange(event.target.value);
          }}
        >
          {hasEmptyOption ? null : (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </FieldShell>
    );
  }

  const selected = options.find((option) => option.value === value);
  return (
    <FieldShell
      label={label}
      required={required}
      error={error}
      hint={disabledReason ?? hint}
      labelHidden={labelHidden}
      inputId={id}
    >
      <input
        id={id}
        list={disabled ? undefined : listId}
        defaultValue={selected?.label ?? ''}
        required={required}
        disabled={disabled}
        placeholder={placeholder}
        aria-invalid={error !== undefined || undefined}
        className={clsx(
          INPUT_CLASSES,
          error !== undefined ? 'border-red-fg' : 'border-control-line',
          disabled ? 'cursor-not-allowed opacity-60' : '',
        )}
        onChange={(event) => {
          const match = options.find((option) => option.label === event.target.value);
          if (match !== undefined) onChange(match.value);
        }}
      />
      <datalist id={listId}>
        {options.map((option) => (
          <option key={option.value} value={option.label} />
        ))}
      </datalist>
    </FieldShell>
  );
}

/** Calendar-fact input (lease dates) — a date, not an instant (docs/02). */
/**
 * A time of day, as a native `<input type="time">`.
 *
 * Native rather than a custom picker: it gives every platform's own control —
 * a proper wheel on iOS, a keyboard-friendly spinner on desktop — for free, and
 * it emits exactly the `HH:MM` 24-hour string the contract expects, so nothing
 * on the way to the server has to parse or reformat. What the USER sees is
 * their locale's format (12-hour with AM/PM in en-US, which is what TKC wants);
 * what the app stores is unambiguous. A hand-rolled dropdown would have to
 * reimplement both halves and would get the locale one wrong.
 */
export function TimeField({
  label,
  required,
  error,
  hint,
  labelHidden,
  value,
  onChange,
  disabled,
}: FieldShellProps & {
  /** 24-hour `HH:MM`, or '' for empty. */
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly disabled?: boolean | undefined;
}): ReactNode {
  const id = useId();
  return (
    <FieldShell
      label={label}
      required={required}
      error={error}
      hint={hint}
      labelHidden={labelHidden}
      inputId={id}
    >
      <input
        id={id}
        type="time"
        value={value}
        required={required}
        disabled={disabled}
        aria-invalid={error !== undefined || undefined}
        className={clsx(
          INPUT_CLASSES,
          error !== undefined ? 'border-red-fg' : 'border-control-line',
          disabled === true && 'cursor-not-allowed opacity-50',
        )}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
    </FieldShell>
  );
}

export function DateField({
  label,
  required,
  error,
  hint,
  labelHidden,
  value,
  onChange,
}: FieldShellProps & {
  readonly value: string;
  readonly onChange: (value: string) => void;
}): ReactNode {
  const id = useId();
  return (
    <FieldShell
      label={label}
      required={required}
      error={error}
      hint={hint}
      labelHidden={labelHidden}
      inputId={id}
    >
      <input
        id={id}
        type="date"
        value={value}
        required={required}
        aria-invalid={error !== undefined || undefined}
        className={clsx(
          INPUT_CLASSES,
          error !== undefined ? 'border-red-fg' : 'border-control-line',
        )}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
    </FieldShell>
  );
}

/**
 * A checkbox with a real label, a hint, and — the reason this exists —
 * a `disabledReason`.
 *
 * Four screens hand-rolled `<input type="checkbox">` before this, which meant
 * four slightly different sizes, no consistent hint slot, and one checkbox that
 * went dead when an account was disabled with nothing on screen to say why.
 * `Button` has enforced `disabledReason` all along (docs/03 §4, docs/04 §7:
 * "disabled buttons without reasons" is a listed anti-pattern); a checkbox that
 * silently stops working is the same defect with less feedback.
 */
export function CheckboxField({
  label,
  checked,
  onChange,
  hint,
  disabledReason,
}: {
  readonly label: string;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly hint?: string | undefined;
  /** Present ⇒ disabled, and the reason is shown rather than left to guesswork. */
  readonly disabledReason?: string | undefined;
}): ReactNode {
  const disabled = disabledReason !== undefined;
  return (
    <div className="flex flex-col gap-1">
      <label className="flex min-h-11 items-center gap-2 text-body text-ink">
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          title={disabledReason}
          onChange={(event) => {
            onChange(event.target.checked);
          }}
          className={
            'keeper-ignore size-4 rounded-ctl border-control-line text-brand-700 ' +
            'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-700 ' +
            (disabled ? 'cursor-not-allowed opacity-60' : '')
          }
        />
        <span className={disabled ? 'text-ink2' : undefined}>{label}</span>
      </label>
      {(disabledReason ?? hint) ? (
        <span className="text-chip text-ink2">{disabledReason ?? hint}</span>
      ) : null}
    </div>
  );
}
